import Quill, {Parchment} from "quill";
import {EmbedBlot, Root} from "parchment";
import {v4 as uuid} from "uuid";
import {DocumentService} from "../../service/document.service";
import {Project} from "../../api/db/entities/Project";
import {ProjectSchemaDisplay, ProjectSchemas} from "../../api/project_schemas";
import {DataService} from "../../service/data.service";
import {EditorComponent} from "../editor/editor.component";
import {ErrorService} from "../../service/error.service";
import {MetadataBlot} from "./MetadataBlot";

// Notes: Private notes - ROLE BASED - in comments
// Open and close sections - FULL SECTION and/or metadata
// Sidebar menu format -> multiple panels of standard theme resource display
//   Unread comments listed under the sections they are "in" - click to jump
// NOTES -> rather than being metatdata, it is a section type - lowest level so available in al places (Schema parts needs new attribute - EXPORTED: boolean default true.  Notes would be false - icon pi-lightbulb

// Metadata - all but notes get synopsis metadata, and a title field that pulls to name and level


export class SectionBlot extends EmbedBlot {

    static override className = 'rng-section';
    static override blotName = 'section';
    static override tagName = 'span';

    section: Section;
    sectionNode: HTMLSpanElement;
    toggleI: HTMLElement;
    metadataToggleI: HTMLElement;
    label: HTMLSpanElement;
    labelHolder: HTMLSpanElement;
    metadataDiv: HTMLDivElement;
    public static USER_UUID: string;

    constructor(scroll: Root, domNode) {
        super(scroll, domNode);

        if (!SectionBlot.USER_UUID) {
            SectionBlot.USER_UUID = DataService.s_user.uuid;
        }

        this.section = JSON.parse(domNode.getAttribute('section-data'));

        this.sectionNode = domNode;

        this.label = document.createElement("span");
        this.labelHolder = document.createElement("span");
        this.labelHolder.style.fontSize = 'large';
        this.labelHolder.style.fontWeight = 'bold';
        this.labelHolder.style.display = 'flex';
        this.labelHolder.style.justifyContent = "center"
        this.labelHolder.style.width = '100%';
        this.labelHolder.appendChild(this.label);

        this.toggleI = document.createElement('i');
        this.toggleI.setAttribute('contenteditable', 'false');
        this.toggleI.style.cursor = 'pointer';
        this.toggleI.style.fontSize = 'large';
        this.toggleI.style.fontWeight = 'bold';
        this.toggleI.style.float = 'left';
        this.setToggleClassList();
        this.toggleI.addEventListener('click', function(e) {
            const blot: Parchment.Blot = <Parchment.Blot>Quill.find(domNode);

            window['toggleSection'].zone.run(
                () => {
                    window['toggleSection'].toggleSection(blot);
                }
            );
        });

        this.metadataToggleI = document.createElement('i');
        this.metadataToggleI.setAttribute('contenteditable', 'false');
        this.metadataToggleI.style.cursor = 'pointer';
        this.metadataToggleI.style.fontSize = 'smaller';
        this.metadataToggleI.style.verticalAlign = 'super';
        this.setMetadataToggleClassList();
        this.metadataToggleI.addEventListener('click', function(e) {
            const blot: Parchment.Blot = <Parchment.Blot>Quill.find(domNode);

            window['toggleSectionMetadata'].zone.run(
                () => {
                    window['toggleSectionMetadata'].toggleSectionMetadata(blot);
                }
            );
        });
        this.labelHolder.appendChild(this.metadataToggleI);

        this.metadataDiv = document.createElement('div');
        this.metadataDiv.setAttribute('contenteditable', 'false');
        const hr = document.createElement('hr');
        hr.style.width = '50%';
        hr.style.marginLeft = '25%';
        hr.style.height = '3px';
        hr.style.color = 'black';
        this.metadataDiv.appendChild(hr);
        const metadataTableDiv = document.createElement('div');

        //The div is not editable - make the fields editable specifically as needed
        metadataTableDiv.setAttribute('contenteditable', 'false');
        const table = document.createElement('table');
        const blurListener = function(e: FocusEvent) {
            const blot: Parchment.Blot = <Parchment.Blot>Quill.find(domNode);

            window['sectionMetadataFocusListener'].zone.run(
                () => {
                    window['sectionMetadataFocusListener'].blurListener(blot, e);
                }
            );
        }
        table.appendChild(this.createEntryRow('Name/Title:', '<span id="section.'+this.section.uuid+'_core:name" contenteditable="true">'+this.section.name+'</span>', [{eventName: 'focusout', listener: blurListener}]));
        metadataTableDiv.appendChild(table);

        domNode.appendChild(document.createElement('hr'));
        domNode.appendChild(this.toggleI);
        domNode.appendChild(this.labelHolder);
        this.updateSectionIconAndLabel();
        domNode.appendChild(this.metadataDiv);
        domNode.appendChild(document.createElement('hr'));

        this.setMetadataToggleClassList();
        this.setToggleClassList();
    }

    private createEntryRow(label: string, content: string, listeners: {
        eventName: string;
        listener: (e: FocusEvent) => void;
    }[]) {
        const tr = document.createElement('tr');
        const labelTd = document.createElement('td');
        labelTd.setAttribute('contenteditable', 'false');
        labelTd.textContent = label;
        tr.appendChild(labelTd);
        const contentTd = document.createElement('td');
        contentTd.setAttribute('contenteditable', 'false');
        contentTd.innerHTML = content;
        const contentElement = contentTd.firstChild;
        for (const listener of listeners) {
            contentElement.addEventListener(listener.eventName, listener.listener);
        }
        tr.appendChild(contentTd);
        return tr;
    }

    static blurListener(sectionBlot: SectionBlot, e: FocusEvent) {
        const element: any = <HTMLSpanElement> e.relatedTarget;
        const fieldId: string = element.id.slice(element.id.indexOf('_')+1);
        const namespace: string = fieldId.slice(0, fieldId.indexOf(':'));
        const field: string = fieldId.slice(fieldId.indexOf(':'));
        switch (namespace) {
            case 'core':
                //Core members of the section object
                switch (field) {
                    case 'name':
                        sectionBlot.section.name = element.text();
                        break;
                    case 'level':
                        sectionBlot.section.level = Number.parseInt(element.firstElementChild.nodeValue,10);
                        break;
                    default: console.log("ERROR: field "+field+" not found in core namespace - Please report to RuneAndGear - this needs fixed. . . ");
                }
                break;

            case 'metadata':
                //Section object metadata
                const schemaDisplay: ProjectSchemaDisplay = DocumentService.projectSchemaDisplay;
                if (schemaDisplay.schema.sectionLayout[sectionBlot.section.level].metadataKeys.indexOf(field) != -1) {
                    sectionBlot.section.metadata[field] = element.text();
                } else {
                    console.log("ERROR: field "+field+" not found in metadata namespace ("+
                        schemaDisplay.schema.sectionLayout[sectionBlot.section.level].metadataKeys+
                        " are available in "+schemaDisplay.displayName+"["+sectionBlot.section.level+
                        "]) - Please report to RuneAndGear - this needs fixed. . . ");
                }
                break;

            default: //Add more cases for Custom fields from the Book Designer, editor, author, etc. . . . TODO
                console.log("ERROR: namespace "+namespace+" not found - Please report to RuneAndGear - this needs fixed. . . ");
        }

    }

    static toggleSection(sectionBlot: SectionBlot) {
        console.log('toggleSection -- ', sectionBlot);

        sectionBlot.section.sectionIsOpen[SectionBlot.USER_UUID] = (! sectionBlot.section.sectionIsOpen[SectionBlot.USER_UUID]);
        sectionBlot.setToggleClassList();
    }

    static toggleSectionMetadata(sectionBlot: SectionBlot) {
        console.log('toggleSectionMetadata -- ', sectionBlot);

        sectionBlot.section.metadataIsOpen[SectionBlot.USER_UUID] = (! sectionBlot.section.metadataIsOpen[SectionBlot.USER_UUID]);
        sectionBlot.setMetadataToggleClassList();
    }

    private setToggleClassList() {
        if (!this.toggleI) {
            return;
        }
        this.toggleI.className = 'section-toggle-icon ' +
           'section-toggle-icon-'+this.section.uuid + ' pi '+
           'pi-chevron-'+((this.section.sectionIsOpen[SectionBlot.USER_UUID])?'down':'right');
        const startIndex: number = EditorComponent.quill.getIndex(this)+2;
        const blotNodes: HTMLCollectionOf<Element> = document.getElementsByClassName(SectionBlot.className);
        let endIndex: number;
        for (let i: number = 0; i < blotNodes.length; i++) {
            const blot = <SectionBlot> Quill.find(blotNodes[i]);
            const blotIndex: number = EditorComponent.quill.getIndex(blot);
            if (blotIndex > startIndex && blot.section.level <= this.section.level) {
                endIndex = blotIndex;
                break;
            }
        }

        if (startIndex && !endIndex) { //Ran to the end of the file. . .
            endIndex = EditorComponent.quill.getLength();
        }

        const selection = EditorComponent.quill.getSelection();
        console.log('Indices: ', startIndex, endIndex, endIndex-startIndex);
        EditorComponent.quill.formatText(startIndex, endIndex-startIndex, {
            'hidden': this.section.sectionIsOpen[SectionBlot.USER_UUID]?'':'none'
        });
        EditorComponent.quill.setSelection(selection);
        console.log('RESULT: ', EditorComponent.quill.getContents());
    }

    private setMetadataToggleClassList() {
        console.log('toggleMetadataToggle -- ', SectionBlot.USER_UUID, this.metadataToggleI, this.metadataDiv);
        if (this.metadataToggleI != null) {
            this.metadataToggleI.className = 'section-metadata-toggle-icon' +
                ' section-metadata-toggle-icon-' + this.section.uuid + ' pi' +
                ' pi-' + ((this.section.metadataIsOpen[SectionBlot.USER_UUID]) ? 'minus' : 'plus') + '-circle';
        }
        if (this.metadataDiv != null) {
            this.metadataDiv.className = 'section-metadata' +
                ' section-metadata-' + this.section.uuid +
                ' section-metadata-' + ((this.section.metadataIsOpen[SectionBlot.USER_UUID]) ? 'open' : 'closed');
        }
    }

    static buildSection(value: string | Section, node) {
        const section: Section = (typeof value === "string")? JSON.parse(value) : value;

        const userUUID = DataService.s_user.uuid;

        if (!section.sectionIsOpen) {
            section.sectionIsOpen = {};
        }
        if (!section.sectionIsOpen[userUUID]) {
            section.sectionIsOpen[userUUID] = true;
        }
        if (!section.metadataIsOpen) {
            section.metadataIsOpen = {};
        }
        if (!section.metadataIsOpen[userUUID]) {
            section.metadataIsOpen[userUUID] = false;
        }

        console.log("buildSection -- section ", section);

        node.setAttribute('section-data', JSON.stringify(section));
        node.setAttribute('section-level', section.level);
        //Use this to locate a tag in the DOM
        node.id = 'RNGSECTION-'+section.uuid;
        node.classList.add(SectionBlot.className);
        node.classList.add('section-'+section.uuid);
        node.contentEditable = false;
        //node.classList.add('no-reveal-codes');
    }

    static override create(value) {
        let node = super.create(value);

        SectionBlot.buildSection(value, node);
        return node;
    }

    static override value(node) {
        /*if (!node) {
            return null;
        }*/
        console.log('NODE: ', node);
        console.log('NODE DATA: ', node.getAttribute('section-data'));
        return JSON.parse(node.getAttribute('section-data'));
    }

    static getBlotForSection(section: Section): SectionBlot {
        if (section) {
            return this.getBlotForNode(document.getElementById('RNGSECTION-' + section.uuid));
        }
        return null;
    }

    static getBlotForNode(node: Element): SectionBlot {
        if (node) {
            return <SectionBlot>Quill.find(node, true);
        }
        return null;
    }

    public updateSectionIconAndLabel() {
        this.label.innerHTML = DocumentService.projectSchemaDisplay.schema.sectionLayout[this.section.level].tocIconHTML + ' ' + SectionBlot.getSectionLabel(this.section) + ' ';
    }

    public static getSectionLabel(section: Section) {
        return DocumentService.getSectionString(section)+( (section.name != null) ? ': '+section.name : '');
    }

    //Prototype javascript function to wrap in a div -
    // needs modified to only wrap if not already wrapped, so it based on the section blot, etc.
    wrapElementsBetween(startElem, endElem) {
        const wrapper = document.createElement('div');
        let currentElem = startElem.nextElementSibling;

        while (currentElem && currentElem !== endElem) {
            wrapper.appendChild(currentElem);
            currentElem = currentElem.nextElementSibling;
        }
        startElem.parentNode.insertBefore(wrapper, endElem);
    }

}

export class Section {
    uuid: string;
    level: number;
    name: string | null;
    metadata: any;
    sectionIsOpen: { [id: string]: boolean} = {};
    metadataIsOpen: { [id: string]: boolean} = {};
    caretIndex?: number;

    static createInitialSection(project: Project): Section {
        console.log("CreateInitialSection: ", project);
        const userUUID = DataService.s_user.uuid;
        let newSection: Section = {
            uuid: uuid(),
            level: DocumentService.projectSchemaDisplay ?
                DocumentService.projectSchemaDisplay.schema.projectLevel :
                ProjectSchemas.SCHEMADISPLAY_BY_CODE[project.schemaCode].schema.projectLevel,
            name: project.title,
            metadata: {},
            sectionIsOpen: {},
            metadataIsOpen: {}
        };
        newSection.sectionIsOpen[userUUID] = true;
        newSection.metadataIsOpen[userUUID] = false;
        return newSection;
    }

    static getInitialSectionHTML(project: Project): string {
        const section: Section = this.createInitialSection(project);

        return "<" + SectionBlot.tagName + " " +
            "id='RNGSECTION-" + section.uuid + "' " +
            "class='" + SectionBlot.className + " section-" + section.uuid + "' " +
            "section-data='" + JSON.stringify(section) + "' " +
            "section-level='" + section.level + "' " +
            "content-editable=false " +
            "/>";
    }
}
